import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { useSelector } from 'react-redux';
import { RootState } from '../store';
import { setSearch } from './publicSlice';
import {
  BaseReportFilterState,
  DisputeFilterState,
  OrdersFilterState,
  TotalRevenueFilterState,
  OrdersBudgetFilterState,
  ReportFiltersState,
  UsersFilterState,
  SubscriptionFilterState,
} from '@/types/reportFilters';

// Re-export types so consumers can import from either location if needed
export * from '@/types/reportFilters';

// ==========================================
// Base Initial State (Common shared defaults)
// ==========================================
export const initialBaseFilterState: BaseReportFilterState = {
  fromDate: '',
  toDate: '',
  search: '',
  limit: 10,
};

// ==========================================
// Initial States per Report
// ==========================================
export const initialDisputeFilterState: DisputeFilterState = {
  ...initialBaseFilterState,
  requesterRole: [],
  method: [],
  status: [],
};

export const initialOrdersFilterState: OrdersFilterState = {
  ...initialBaseFilterState,
  status: [],
  orderType: [],
  orderMethod: [],
};

export const initialTotalRevenueFilterState: TotalRevenueFilterState = {
  ...initialBaseFilterState,
  status: [],
  requestTypes: [],
  method: [],
};

export const initialOrdersBudgetFilterState: OrdersBudgetFilterState = {
  ...initialBaseFilterState,
  status: [],
  methods: [],
};

export const initialUsersFilterState: UsersFilterState = {
  ...initialBaseFilterState,
  profileTypes: [],
  location: '',
};

export const initialSubscriptionFilterState: SubscriptionFilterState = {
  ...initialBaseFilterState,
  periodType: [],
  planType: [],
};

const initialState: ReportFiltersState = {
  period: '',
  dispute: initialDisputeFilterState,
  orders: initialOrdersFilterState,
  totalRevenue: initialTotalRevenueFilterState,
  ordersBudget: initialOrdersBudgetFilterState,
  users: initialUsersFilterState,
  subscription: initialSubscriptionFilterState,
};

// ==========================================
// Slice Definition
// ==========================================
export const reportFiltersSlice = createSlice({
  name: 'reportFilters',
  initialState,
  reducers: {
    // --- Global Actions (Reset all filters) ---
    resetAllReportFilters: (
      state,
      action: PayloadAction<string | undefined>
    ) => {
      state.period = action.payload || '';
      state.dispute = { ...initialDisputeFilterState };
      state.orders = { ...initialOrdersFilterState };
      state.totalRevenue = { ...initialTotalRevenueFilterState };
      state.ordersBudget = { ...initialOrdersBudgetFilterState };
      state.users = { ...initialUsersFilterState };
      state.subscription = { ...initialSubscriptionFilterState };
    },

    updateReportDateFilters: (
      state,
      action: PayloadAction<{ fromDate?: string; toDate?: string }>
    ) => {
      const { fromDate, toDate } = action.payload;
      state.dispute = { ...state.dispute, fromDate, toDate };
      state.orders = { ...state.orders, fromDate, toDate };
      state.totalRevenue = { ...state.totalRevenue, fromDate, toDate };
      state.ordersBudget = { ...state.ordersBudget, fromDate, toDate };
      state.users = { ...state.users, fromDate, toDate };
      state.subscription = { ...state.subscription, fromDate, toDate };
    },

    // --- Dispute Actions ---
    setDisputeFilters: (
      state,
      action: PayloadAction<Partial<DisputeFilterState>>
    ) => {
      state.dispute = {
        ...state.dispute,
        ...action.payload,
      };
    },
    resetDisputeFilters: (state) => {
      state.dispute = {
        ...initialDisputeFilterState,
        fromDate: state.dispute.fromDate,
        toDate: state.dispute.toDate,
      };
    },

    // --- Orders Actions ---
    setOrdersFilters: (
      state,
      action: PayloadAction<Partial<OrdersFilterState>>
    ) => {
      state.orders = {
        ...state.orders,
        ...action.payload,
      };
    },
    resetOrdersFilters: (state) => {
      state.orders = {
        ...initialOrdersFilterState,
        fromDate: state.orders.fromDate,
        toDate: state.orders.toDate,
      };
    },

    // --- Total Revenue Actions ---
    setTotalRevenueFilters: (
      state,
      action: PayloadAction<Partial<TotalRevenueFilterState>>
    ) => {
      state.totalRevenue = {
        ...state.totalRevenue,
        ...action.payload,
      };
    },
    resetTotalRevenueFilters: (state) => {
      state.totalRevenue = {
        ...initialTotalRevenueFilterState,
        fromDate: state.totalRevenue.fromDate,
        toDate: state.totalRevenue.toDate,
      };
    },

    // --- Orders Budget Actions ---
    setOrdersBudgetFilters: (
      state,
      action: PayloadAction<Partial<OrdersBudgetFilterState>>
    ) => {
      state.ordersBudget = {
        ...state.ordersBudget,
        ...action.payload,
      };
    },
    resetOrdersBudgetFilters: (state) => {
      state.ordersBudget = {
        ...initialOrdersBudgetFilterState,
        fromDate: state.ordersBudget.fromDate,
        toDate: state.ordersBudget.toDate,
      };
    },

    // --- Users Actions ---
    setUsersFilters: (
      state,
      action: PayloadAction<Partial<UsersFilterState>>
    ) => {
      state.users = {
        ...state.users,
        ...action.payload,
      };
    },
    resetUsersFilters: (state) => {
      state.users = {
        ...initialUsersFilterState,
        fromDate: state.users.fromDate,
        toDate: state.users.toDate,
      };
    },

    // --- Subscription Actions ---
    setSubscriptionFilters: (
      state,
      action: PayloadAction<Partial<SubscriptionFilterState>>
    ) => {
      state.subscription = {
        ...state.subscription,
        ...action.payload,
      };
    },
    resetSubscriptionFilters: (state) => {
      state.subscription = {
        ...initialSubscriptionFilterState,
        fromDate: state.subscription.fromDate,
        toDate: state.subscription.toDate,
      };
    },

    // --- Period Actions ---
    setReportPeriod: (state, action: PayloadAction<string>) => {
      state.period = action.payload;
    },
    resetReportPeriod: (state) => {
      state.period = '';
    },
  },
});

// Export Action Creators
export const {
  // Global
  resetAllReportFilters,
  updateReportDateFilters,

  // Period
  setReportPeriod,
  resetReportPeriod,

  // Dispute
  setDisputeFilters,
  resetDisputeFilters,

  // Orders
  setOrdersFilters,
  resetOrdersFilters,

  // Total Revenue
  setTotalRevenueFilters,
  resetTotalRevenueFilters,

  // Orders Budget
  setOrdersBudgetFilters,
  resetOrdersBudgetFilters,

  // Users
  setUsersFilters,
  resetUsersFilters,

  // Subscription
  setSubscriptionFilters,
  resetSubscriptionFilters,
} = reportFiltersSlice.actions;

// Selectors / Custom Hooks
export const useReportFiltersState = () =>
  useSelector((state: RootState) => state.reportFilters);

export const useReportPeriod = () =>
  useSelector((state: RootState) => state.reportFilters?.period || '');

export const useDisputeFilters = () =>
  useSelector(
    (state: RootState) =>
      state.reportFilters?.dispute || initialDisputeFilterState
  );

export const useOrdersFilters = () =>
  useSelector(
    (state: RootState) =>
      state.reportFilters?.orders || initialOrdersFilterState
  );

export const useTotalRevenueFilters = () =>
  useSelector(
    (state: RootState) =>
      state.reportFilters?.totalRevenue || initialTotalRevenueFilterState
  );

export const useOrdersBudgetFilters = () =>
  useSelector(
    (state: RootState) =>
      state.reportFilters?.ordersBudget || initialOrdersBudgetFilterState
  );

export const useUsersFilters = () =>
  useSelector(
    (state: RootState) => state.reportFilters?.users || initialUsersFilterState
  );

export const useSubscriptionFilters = () =>
  useSelector(
    (state: RootState) =>
      state.reportFilters?.subscription || initialSubscriptionFilterState
  );

export default reportFiltersSlice.reducer;
